import * as THREE from 'three';
import { events } from '../core/events.js';
import { groundRadius } from './placement.js';
import { colliders } from './colliders.js';

// The player's crashed landing pod — a fixed narrative landmark placed on the
// far side of the planet. Tilted, partially embedded in a scarred crater with
// debris raycast onto the actual mesh surface (so nothing floats above the
// noise-displaced terrain), plus a glowing hull breach, a blinking strobe,
// and drifting smoke. First time the player comes within range we emit
// 'pod:discovered' so the story system can fire a dialog beat.

const UP_Y = new THREE.Vector3(0, 1, 0);
// Unit direction where the pod sits — 25° arc from spawn along the
// player's initial forward direction (+X), so it's just ahead and a
// short walk from where you wake up.
export const POD_DIRECTION = new THREE.Vector3(
  Math.sin(25 * Math.PI / 180), Math.cos(25 * Math.PI / 180), 0,
).normalize();
const DISCOVERY_RANGE = 5.5;
const INTERACT_RANGE  = 3.2;        // tighter — player must stand next to the pod

let podWorldPos = null;
let discovered  = false;

// Per-pod dynamic elements kept in module scope for updates.
let strobe = null;
const smokePuffs = [];

export function createLandingPod(scene, planet) {
  const group = new THREE.Group();

  // ---- Place the group on the planet surface FIRST so ground-snap can
  // compute real world directions for each debris piece ----
  const groundR = groundRadius(planet, POD_DIRECTION);
  group.quaternion.setFromUnitVectors(UP_Y, POD_DIRECTION);
  group.position.copy(POD_DIRECTION).multiplyScalar(groundR);
  scene.add(group);

  // Pre-compute the inverse quaternion once for the ground-snap helper.
  const invQuat = group.quaternion.clone().invert();
  const _tmpLocal = new THREE.Vector3();
  const _tmpWorld = new THREE.Vector3();

  // Raycast a local tangent point (localX, _, localZ) onto the real mesh
  // surface, converting back into group-local coordinates. `hover` lifts the
  // mesh slightly above the ground; negative values embed it.
  function snapToGround(mesh, localX, localZ, hover = 0) {
    _tmpLocal.set(localX, 0, localZ).applyQuaternion(group.quaternion);
    _tmpWorld.copy(group.position).add(_tmpLocal);
    const dir = _tmpWorld.clone().normalize();
    const gr = groundRadius(planet, dir);
    const worldTarget = dir.multiplyScalar(gr + hover);
    const local = worldTarget.sub(group.position).applyQuaternion(invQuat);
    mesh.position.copy(local);
  }

  // ---- Materials ----
  const hullMat = new THREE.MeshStandardMaterial({
    color: 0xb8bcc2, roughness: 0.7, metalness: 0.35, flatShading: true,
  });
  const burntHullMat = new THREE.MeshStandardMaterial({
    color: 0x4a423c, roughness: 0.95, metalness: 0.2, flatShading: true,
  });
  const shieldMat = new THREE.MeshStandardMaterial({
    color: 0x2c1208, roughness: 1, metalness: 0.1, flatShading: true,
  });
  const trimMat = new THREE.MeshStandardMaterial({
    color: 0x2a2e38, roughness: 0.85, metalness: 0.3,
  });
  const hatchMat = new THREE.MeshStandardMaterial({
    color: 0x3c3e48, roughness: 0.7, metalness: 0.4,
  });
  const windowMat = new THREE.MeshStandardMaterial({
    color: 0x0e1018, roughness: 0.2, metalness: 0.95,
    emissive: 0x2a5080, emissiveIntensity: 0.22,
  });
  // Warm, earthy palette — the impact exposed soil; no grass-tinted outer
  // ring (it read as discoloration instead of damage).
  const scorchDirt  = new THREE.MeshStandardMaterial({ color: 0x3c2a18, roughness: 1 });
  const deepCharMat = new THREE.MeshStandardMaterial({ color: 0x1a130c, roughness: 1 });
  const earthMat = new THREE.MeshStandardMaterial({
    color: 0x3a2815, roughness: 1, flatShading: true,          // dirt brown
  });
  const debrisMat = new THREE.MeshStandardMaterial({
    color: 0x7a7e86, roughness: 0.6, metalness: 0.3, flatShading: true,
  });

  // ---- Impact site: exposed dirt with a small char center ----
  // Noise-jittered circle gives an irregular rim so it reads as exposed earth
  // rather than a graphic shape on the grass.
  function makePatch(radius, segments, jitter, seed, material) {
    const geo = new THREE.CircleGeometry(radius, segments);
    const pos = geo.attributes.position;
    for (let i = 1; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const angle = Math.atan2(z, x);
      const n = Math.sin(angle * 3.0 + seed) * 0.6
              + Math.sin(angle * 7.0 + seed * 1.7) * 0.4;
      const f = 1 + n * jitter;
      pos.setX(i, x * f);
      pos.setZ(i, z * f);
    }
    pos.needsUpdate = true;
    return new THREE.Mesh(geo, material);
  }

  // Main impact patch — built from a central disc plus a few overlapping
  // satellite discs. Low per-patch jitter keeps each lobe rounded; the
  // overlap creates the overall organic silhouette without any single patch
  // going lopsided.
  const dirtCentre = makePatch(1.3, 40, 0.08, 1.3, scorchDirt);
  dirtCentre.rotation.x = -Math.PI / 2;
  dirtCentre.position.set(0, -0.03, 0);
  dirtCentre.receiveShadow = true;
  group.add(dirtCentre);

  const satelliteOffsets = [
    [0.8, 0.5,  0.95, 2.1],
    [-0.7, 0.6, 0.85, 3.4],
    [0.2, -0.8, 0.9,  5.0],
    [-0.6, -0.4, 0.8, 0.9],
  ];
  for (const [ox, oz, sr, seed] of satelliteOffsets) {
    const sat = makePatch(sr, 28, 0.1, seed, scorchDirt);
    sat.rotation.x = -Math.PI / 2;
    sat.position.set(ox, -0.028, oz);
    group.add(sat);
  }

  // Char impact — small, roundish, right at the pod base.
  const charCenter = makePatch(0.7, 36, 0.1, 4.1, deepCharMat);
  charCenter.rotation.x = -Math.PI / 2;
  charCenter.position.y = -0.018;
  group.add(charCenter);

  // ---- Upturned-dirt crater lip ----
  // Small clumps sitting ON the dirt patch rim so they read as raised earth
  // around the crater — gives the impact a 3D silhouette rather than a
  // flat decal.
  const clumpCount = 10;
  for (let i = 0; i < clumpCount; i++) {
    const angle = (i / clumpCount) * Math.PI * 2 + Math.random() * 0.35;
    const ra = 1.35 + Math.random() * 0.25;
    const size = 0.13 + Math.random() * 0.09;
    const clump = new THREE.Mesh(new THREE.DodecahedronGeometry(size), earthMat);
    const x = Math.cos(angle) * ra;
    const z = Math.sin(angle) * ra;
    snapToGround(clump, x, z, -size * 0.3);     // partly buried at the lip
    clump.rotation.set(Math.random() * 2, Math.random() * 2, Math.random() * 2);
    clump.scale.setScalar(0.75 + Math.random() * 0.3);
    clump.castShadow = true;
    clump.receiveShadow = true;
    group.add(clump);
  }

  // ---- Tilted pod body (pivots at its base) ----
  // Strong tilt + slight pitch so the silhouette clearly reads as "crashed",
  // not just "upright structure".
  const tilt = new THREE.Group();
  tilt.rotation.z = 0.85;
  tilt.rotation.x = -0.22;
  tilt.position.y = -0.22;      // embed into ground
  group.add(tilt);

  // Flared main hull — wider base, narrower shoulders for a classic pod shape.
  const hull = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.82, 1.1, 28), hullMat);
  hull.position.y = 0.66;
  hull.castShadow = true;
  hull.receiveShadow = true;
  tilt.add(hull);

  // Burnt/scuffed patch on one side of the hull
  const burn = new THREE.Mesh(
    new THREE.CylinderGeometry(0.49, 0.83, 1.1, 28, 1, false,
      Math.PI * 0.2, Math.PI * 0.55),
    burntHullMat,
  );
  burn.position.y = 0.66;
  tilt.add(burn);

  // Panel seam bands — two thin rings around the hull give it mechanical detail.
  for (const [y, r1, r2] of [[0.38, 0.7, 0.72], [0.92, 0.54, 0.56]]) {
    const band = new THREE.Mesh(
      new THREE.CylinderGeometry(r1, r2, 0.035, 28), trimMat,
    );
    band.position.y = y;
    tilt.add(band);
  }

  // Heat shield — taller and more visible at the base
  const shield = new THREE.Mesh(new THREE.CylinderGeometry(0.84, 0.82, 0.2, 28), shieldMat);
  shield.position.y = 0.1;
  shield.castShadow = true;
  tilt.add(shield);

  // Thruster ring at the base with three nozzles splaying outward
  const nozzleMat = new THREE.MeshStandardMaterial({
    color: 0x1a1a1f, roughness: 0.6, metalness: 0.7, flatShading: true,
  });
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + Math.PI / 2;
    const nozzle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.17, 0.18, 10), nozzleMat,
    );
    nozzle.position.set(Math.cos(a) * 0.6, 0.02, Math.sin(a) * 0.6);
    nozzle.castShadow = true;
    tilt.add(nozzle);
  }

  // Trim ring above the shield
  const trim = new THREE.Mesh(new THREE.CylinderGeometry(0.83, 0.83, 0.04, 28), trimMat);
  trim.position.y = 0.22;
  tilt.add(trim);

  // Nose cap
  const nose = new THREE.Mesh(new THREE.ConeGeometry(0.48, 0.32, 28), hullMat);
  nose.position.y = 1.37;
  nose.castShadow = true;
  tilt.add(nose);

  // ---- Landing legs — three splayed struts, one bent/broken ----
  const legMat = new THREE.MeshStandardMaterial({
    color: 0x3a3e48, roughness: 0.7, metalness: 0.5, flatShading: true,
  });
  const footMat = new THREE.MeshStandardMaterial({
    color: 0x2a2c34, roughness: 0.8, metalness: 0.4,
  });
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const broken = i === 1;                       // second leg is bent
    const legGroup = new THREE.Group();
    legGroup.position.set(Math.cos(a) * 0.8, 0.18, Math.sin(a) * 0.8);
    legGroup.rotation.y = a + Math.PI / 2;        // face outward
    tilt.add(legGroup);

    // Upper strut angles from hull down-and-out
    const upper = new THREE.Mesh(
      new THREE.CylinderGeometry(0.035, 0.035, 0.55, 8), legMat,
    );
    upper.rotation.z = broken ? -0.15 : 0.5;
    upper.position.set(broken ? 0.02 : -0.12, broken ? -0.05 : -0.18, 0);
    upper.castShadow = true;
    legGroup.add(upper);

    // Lower strut (skipped on the broken leg to make the break readable)
    if (!broken) {
      const lower = new THREE.Mesh(
        new THREE.CylinderGeometry(0.028, 0.028, 0.3, 8), legMat,
      );
      lower.rotation.z = 0.15;
      lower.position.set(-0.24, -0.45, 0);
      lower.castShadow = true;
      legGroup.add(lower);

      const foot = new THREE.Mesh(
        new THREE.CylinderGeometry(0.09, 0.11, 0.05, 12), footMat,
      );
      foot.position.set(-0.27, -0.58, 0);
      foot.castShadow = true;
      legGroup.add(foot);
    }
  }

  // Hatch + seam
  const hatch = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.5, 0.36), hatchMat);
  hatch.position.set(0.66, 0.75, 0);
  hatch.castShadow = true;
  tilt.add(hatch);
  const hatchSeam = new THREE.Mesh(new THREE.BoxGeometry(0.008, 0.42, 0.3), trimMat);
  hatchSeam.position.set(0.685, 0.75, 0);
  tilt.add(hatchSeam);

  // Window + frame
  const windowMesh = new THREE.Mesh(new THREE.CircleGeometry(0.11, 24), windowMat);
  windowMesh.position.set(-0.7, 0.85, 0);
  windowMesh.rotation.y = -Math.PI / 2;
  tilt.add(windowMesh);
  const windowFrame = new THREE.Mesh(
    new THREE.TorusGeometry(0.12, 0.012, 8, 24),
    trimMat,
  );
  windowFrame.position.set(-0.693, 0.85, 0);
  windowFrame.rotation.y = Math.PI / 2;
  tilt.add(windowFrame);

  // Hull breach + dangling wires
  const breachInnerMat = new THREE.MeshStandardMaterial({
    color: 0x0a0a0e, roughness: 1,
    emissive: 0x7a2010, emissiveIntensity: 0.9,
  });
  const breach = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.28, 0.14), breachInnerMat);
  breach.position.set(-0.52, 0.5, 0.4);
  breach.rotation.y = -0.35;
  tilt.add(breach);

  const wireMat = new THREE.MeshStandardMaterial({ color: 0x1a1812, roughness: 1 });
  for (let i = 0; i < 4; i++) {
    const wire = new THREE.Mesh(
      new THREE.CylinderGeometry(0.011, 0.011, 0.22 + Math.random() * 0.12, 5),
      wireMat,
    );
    wire.position.set(
      -0.52 + (Math.random() - 0.5) * 0.12,
      0.4 - Math.random() * 0.05,
      0.43 + (Math.random() - 0.5) * 0.08,
    );
    wire.rotation.z = 0.25 + Math.random() * 0.25;
    wire.rotation.x = (Math.random() - 0.5) * 0.2;
    tilt.add(wire);
  }

  // Emergency strobe
  const strobeMat = new THREE.MeshStandardMaterial({
    color: 0xff3828,
    emissive: 0xff3828,
    emissiveIntensity: 0.4,
  });
  strobe = new THREE.Mesh(new THREE.SphereGeometry(0.045, 14, 10), strobeMat);
  strobe.position.set(0, 1.68, 0);
  tilt.add(strobe);
  const strobeCowl = new THREE.Mesh(
    new THREE.TorusGeometry(0.05, 0.012, 8, 18),
    trimMat,
  );
  strobeCowl.position.set(0, 1.68, 0);
  tilt.add(strobeCowl);

  // Broken antenna — mount nub on the nose, antenna pivoted at its base so the
  // snapped lean clearly hinges off the mount rather than floating.
  const antennaMat = new THREE.MeshStandardMaterial({ color: 0x1a1d26, roughness: 0.9 });
  const antennaMount = new THREE.Mesh(
    new THREE.CylinderGeometry(0.035, 0.045, 0.06, 10),
    trimMat,
  );
  antennaMount.position.set(0, 1.56, 0);
  tilt.add(antennaMount);

  const antennaPivot = new THREE.Group();
  antennaPivot.position.set(0, 1.59, 0);
  antennaPivot.rotation.z = 0.85;              // snapped-off lean
  antennaPivot.rotation.x = 0.25;
  tilt.add(antennaPivot);

  const antenna = new THREE.Mesh(
    new THREE.CylinderGeometry(0.013, 0.013, 0.42, 6),
    antennaMat,
  );
  antenna.position.y = 0.21;                   // base at pivot origin
  antennaPivot.add(antenna);

  // Bent kink near the top — small segment angled off the main shaft
  const antennaTip = new THREE.Mesh(
    new THREE.CylinderGeometry(0.012, 0.012, 0.12, 6),
    antennaMat,
  );
  antennaTip.position.set(0.035, 0.44, 0);
  antennaTip.rotation.z = -0.7;
  antennaPivot.add(antennaTip);

  // ---- Scattered debris (all ground-snapped) ----
  const debrisPanel1 = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.04, 0.4), burntHullMat);
  snapToGround(debrisPanel1, -1.6, 1.0, 0.02);
  debrisPanel1.rotation.set(0.1, 0.45, 0.08);
  debrisPanel1.castShadow = true;
  group.add(debrisPanel1);

  const debrisPanel2 = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.03, 0.55), debrisMat);
  snapToGround(debrisPanel2, 1.6, -0.9, 0.02);
  debrisPanel2.rotation.set(-0.06, -0.8, 0.12);
  debrisPanel2.castShadow = true;
  group.add(debrisPanel2);

  // Twisted panel stuck upright in the dirt — snap its base + lift so it
  // emerges from the ground rather than floats.
  const debrisBent = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.32, 0.25), debrisMat);
  snapToGround(debrisBent, -2.1, -0.4, 0.12);
  debrisBent.rotation.set(0.2, 0.3, 0.7);
  debrisBent.castShadow = true;
  group.add(debrisBent);

  // Bent pipe / strut
  const pipe = new THREE.Mesh(
    new THREE.CylinderGeometry(0.028, 0.028, 0.95, 8),
    burntHullMat,
  );
  snapToGround(pipe, 0.9, 1.9, 0.05);
  pipe.rotation.set(0, 0.5, Math.PI / 2 - 0.3);
  pipe.castShadow = true;
  group.add(pipe);

  // Small bolts / scrap pieces scattered in the field
  for (let i = 0; i < 7; i++) {
    const scrap = new THREE.Mesh(
      new THREE.BoxGeometry(0.08 + Math.random() * 0.06, 0.04, 0.12 + Math.random() * 0.08),
      Math.random() < 0.5 ? debrisMat : burntHullMat,
    );
    const a = Math.random() * Math.PI * 2;
    const r = 1.3 + Math.random() * 1.6;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r * 1.4;   // bias scrap along the skid direction
    snapToGround(scrap, x, z, 0.015);
    scrap.rotation.set(Math.random() * 0.4, Math.random() * Math.PI * 2, Math.random() * 0.4);
    scrap.castShadow = true;
    group.add(scrap);
  }

  // ---- Drifting smoke wisps rising from near the breach ----
  smokePuffs.length = 0;
  const smokeMat = () => new THREE.MeshBasicMaterial({
    color: 0x333536,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    fog: true,
  });
  for (let i = 0; i < 4; i++) {
    const puff = new THREE.Mesh(new THREE.SphereGeometry(0.22, 10, 8), smokeMat());
    group.add(puff);
    smokePuffs.push({
      mesh: puff,
      baseX: (Math.random() - 0.5) * 0.3,
      baseZ: (Math.random() - 0.5) * 0.3,
      offset: Math.random() * 4,
    });
  }

  podWorldPos = group.position.clone();
  discovered = false;

  // Collision — a single upright cylinder anchored at the pod base, wide
  // enough to cover the tilted hull silhouette in every direction.
  colliders.push({
    pos: group.position.clone(),
    up:  POD_DIRECTION.clone(),
    r:   1.2,
    h:   1.5,
  });

  return { group, worldPos: podWorldPos };
}

// True when the given world position is within interact range of the pod
// (used by the interaction system to gate the "Repair Communicator" prompt).
export function isPlayerNearPod(worldPos) {
  if (!podWorldPos) return false;
  return worldPos.distanceTo(podWorldPos) < INTERACT_RANGE;
}

export function updateLandingPod(character) {
  if (!discovered && podWorldPos) {
    const dist = character.position.distanceTo(podWorldPos);
    if (dist < DISCOVERY_RANGE) {
      discovered = true;
      events.emit('pod:discovered', { position: podWorldPos.clone() });
    }
  }

  const t = performance.now() / 1000;

  // Emergency strobe — sharp heartbeat-like flash
  if (strobe) {
    const flash = Math.pow(Math.max(0, Math.sin(t * 1.6)), 14);
    strobe.material.emissiveIntensity = 0.4 + flash * 2.2;
  }

  // Smoke wisps — each rises on a 4 s cycle, growing + fading as it goes.
  for (const s of smokePuffs) {
    const cycle = ((t + s.offset) % 4) / 4;
    s.mesh.position.set(s.baseX, 0.4 + cycle * 1.6, s.baseZ);
    s.mesh.scale.setScalar(0.4 + cycle * 1.2);
    s.mesh.material.opacity = Math.sin(cycle * Math.PI) * 0.4;
  }
}
